import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Textarea } from "@/components/ui/textarea";
import { Bookmark, StickyNote, Search, ArrowRight, Save, BookMarked } from "lucide-react";

function resolveIndex(modules) {
  const idx = {};
  (modules || []).forEach((m) =>
    (m.lessons || []).forEach((l) => {
      idx[l.id] = { moduleId: m.id, moduleNumber: m.number, moduleTitle: m.title, lessonTitle: l.title };
    })
  );
  return idx;
}

function NoteCard({ item, onSaveNote, onToggleBookmark }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onSaveNote(item, text);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      data-testid={`note-card-${item.lessonId}`}
      className="rounded-2xl glass p-5 sm:p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-gold truncate">Modul {String(item.moduleNumber).padStart(2, "0")} · {item.moduleTitle}</div>
          <Link to={`/module/${item.moduleId}/lesson/${item.lessonId}`} className="font-heading font-bold text-lg mt-1 block hover:text-gold transition-colors">
            {item.lessonTitle}
          </Link>
        </div>
        <button
          onClick={() => onToggleBookmark(item)}
          title={item.bookmarked ? "Lesezeichen entfernen" : "Merken"}
          data-testid={`notes-bookmark-${item.lessonId}`}
          className="h-9 w-9 flex-shrink-0 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
        >
          <Bookmark className={`h-5 w-5 ${item.bookmarked ? "text-gold fill-gold" : "text-muted-foreground"}`} />
        </button>
      </div>

      {editing ? (
        <div className="mt-3">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid={`notes-edit-${item.lessonId}`}
            placeholder="Ihre Notiz zu dieser Lektion …"
            className="min-h-28 bg-white/5 border-white/10 focus-visible:ring-gold/40"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setText(item.note || ""); setEditing(false); }} className="px-4 py-2 rounded-full glass text-sm hover:text-foreground transition-colors">
              Abbrechen
            </button>
            <button onClick={save} disabled={saving} data-testid={`notes-save-${item.lessonId}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold text-[#0a0d12] text-sm font-semibold disabled:opacity-60 transition-all">
              <Save className="h-3.5 w-3.5" /> {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </div>
      ) : item.note ? (
        <p className="mt-3 text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">{item.note}</p>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground italic">Nur als Lesezeichen gespeichert – noch keine Notiz.</p>
      )}

      <div className="flex items-center justify-between mt-4 pt-3 border-t hairline">
        <button onClick={() => setEditing((v) => !v)} data-testid={`notes-toggle-edit-${item.lessonId}`} className="text-xs text-muted-foreground hover:text-gold inline-flex items-center gap-1 transition-colors">
          <StickyNote className="h-3.5 w-3.5" /> {item.note ? "Notiz bearbeiten" : "Notiz hinzufügen"}
        </button>
        <Link to={`/module/${item.moduleId}/lesson/${item.lessonId}`} className="text-xs text-gold inline-flex items-center gap-1 hover:gap-2 transition-all">
          Zur Lektion <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.div>
  );
}

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "notes", label: "Notizen" },
  { key: "bookmarks", label: "Lesezeichen" },
];

export default function MyNotes() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const courseQ = useQuery({ queryKey: ["course"], queryFn: async () => (await api.get("/course")).data });
  const metaQ = useQuery({ queryKey: ["lesson-meta"], queryFn: async () => (await api.get("/lesson-meta")).data });

  const idx = resolveIndex(courseQ.data?.modules);
  const meta = metaQ.data?.meta || {};

  const counts = {
    all: Object.values(meta).filter((v) => v.note || v.bookmarked).length,
    notes: Object.values(meta).filter((v) => v.note).length,
    bookmarks: Object.values(meta).filter((v) => v.bookmarked).length,
  };

  let items = Object.entries(meta)
    .map(([lessonId, v]) => ({ lessonId, ...v, ...idx[lessonId] }))
    .filter((it) => it.lessonTitle && (it.note || it.bookmarked));
  if (filter === "notes") items = items.filter((it) => it.note);
  if (filter === "bookmarks") items = items.filter((it) => it.bookmarked);
  const term = q.trim().toLowerCase();
  if (term) items = items.filter((it) => `${it.lessonTitle} ${it.moduleTitle || ""} ${it.note || ""}`.toLowerCase().includes(term));
  items.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));

  const saveNote = async (item, text) => {
    try {
      await api.put(`/lesson-meta/${item.moduleId}/${item.lessonId}`, { note: text });
      await qc.invalidateQueries({ queryKey: ["lesson-meta"] });
      toast.success("Notiz gespeichert");
    } catch {
      toast.error("Konnte nicht gespeichert werden.");
    }
  };

  const toggleBookmark = async (item) => {
    try {
      await api.put(`/lesson-meta/${item.moduleId}/${item.lessonId}`, { bookmarked: !item.bookmarked });
      await qc.invalidateQueries({ queryKey: ["lesson-meta"] });
      toast.success(!item.bookmarked ? "Lesezeichen gesetzt" : "Lesezeichen entfernt");
    } catch {
      toast.error("Konnte nicht gespeichert werden.");
    }
  };

  const loading = metaQ.isLoading || courseQ.isLoading;

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-2.5">
            <BookMarked className="h-7 w-7 text-gold" />
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Meine Notizen</h1>
          </div>
          <p className="text-foreground/70 mt-2 max-w-2xl">
            Alle Ihre Lesezeichen und persönlichen Notizen an einem Ort – durchsuchbar und direkt bearbeitbar.
          </p>
        </motion.div>

        {/* Suche + Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 mb-7">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="notes-search"
              placeholder="Notizen, Lektionen oder Module durchsuchen …"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 transition-all"
            />
          </div>
          <div className="flex items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`notes-filter-${f.key}`}
                className={`px-4 py-2.5 rounded-full text-sm transition-all duration-300 ${
                  filter === f.key ? "bg-gold/15 text-gold border border-gold/30" : "glass text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.label} <span className="opacity-60">({counts[f.key]})</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-40 rounded-2xl glass animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div data-testid="notes-empty-state" className="rounded-2xl glass border border-white/10 px-6 py-16 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-2xl bg-gold/10 border border-gold/25 flex items-center justify-center mb-4">
              <StickyNote className="h-7 w-7 text-gold" />
            </div>
            <h3 className="font-heading font-bold text-xl">{counts.all === 0 ? "Noch keine Notizen oder Lesezeichen" : "Keine Treffer"}</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md">
              {counts.all === 0
                ? "Öffnen Sie eine Lektion und tippen Sie oben auf «Merken» oder schreiben Sie unten eine Notiz – sie erscheint dann hier."
                : "Passen Sie Ihre Suche oder den Filter an."}
            </p>
            {counts.all === 0 && (
              <Link to="/dashboard" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300">
                Zum Kurs <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((it) => (
              <NoteCard key={it.lessonId} item={it} onSaveNote={saveNote} onToggleBookmark={toggleBookmark} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
